import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { useCacheMutations } from "@/shared/cache";
import { NARRATIVE_PLACEHOLDER } from "@/shared/utils/narrative";
import { useApi } from "@/shared/utils/request";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import { formatEntryId } from "@/v2/hooks/actions/entryId";
import type { Entry } from "./normalize";

/**
 * Binds the shared v2 stopwatch (`useTimerContext`) to a single time entry so the
 * toolbar timer and the per-row timer buttons act as one control. The stopwatch
 * itself stays generic; this layer owns *which* entry is being timed and the
 * entry-side effects (create a blank entry, persist accumulated duration).
 *
 * Model: `displaySeconds = baseUnitsSeconds + elapsed`, where `baseUnitsSeconds`
 * comes from the server's `startingDurationSeconds` when the span opened. The
 * server owns the duration: `/v2/timer/start` opens a span (closing any previous
 * one) and `/v2/timer/stop` closes it, writing `units_seconds` onto the entry.
 * The local stopwatch is display only — it never PATCHes duration while a span is
 * open, because the server could not tell such a write apart from a user edit.
 *
 * Two contexts by design: the *binding* context (activeEntryId + actions) is
 * stable and only changes on start/stop/pause, so EntriesView and the rows don't
 * re-render every second. The *display* context ticks each second and is consumed
 * only by the small toolbar timer.
 */

interface Binding {
	/** Entry id ("ent_<n>") the running timer is bound to, or null when idle. */
	activeEntryId: string | null;
	/** The entry's total when the span opened; the display counts up from it. */
	baseUnitsSeconds: number;
}

const NO_BINDING: Binding = { activeEntryId: null, baseUnitsSeconds: 0 };

// Only tracked/reviewed entries can host a timer; exported/deleted are inert.
export function canTimeEntry(entry: Entry): boolean {
	return entry.status === "tracked" || entry.status === "reviewed";
}

interface EntriesTimerContextValue {
	/** Entry with the running/paused timer, or null when idle. */
	activeEntryId: string | null;
	isRunning: boolean;
	/** True while a create/persist request is in flight — blocks duplicate clicks. */
	isBusy: boolean;
	/** Start (or switch) the timer onto an existing entry. */
	startForEntry: (entry: Entry) => void;
	/** Create a blank manual entry and start the timer on it. */
	startNewBlankEntry: () => void;
	/** The only "finish" control (pause icon, stop behavior): persist the time
	 * onto the entry and unbind, returning to idle. */
	pause: () => void;
	/** Re-read the running timer from the server (after an edit that may have
	 * rebased its span). */
	syncFromServer: () => Promise<void>;
	/** Drop the timer when its entry is deleted, so it can't keep ticking on a
	 * row that no longer exists. */
	clearIfActive: (entryIds: string[]) => void;
}

const EntriesTimerContext = createContext<EntriesTimerContextValue | undefined>(
	undefined,
);
/** base + elapsed, in seconds — ticks every second; consumed only by the toolbar. */
const EntriesTimerDisplayContext = createContext<number>(0);

export function EntriesTimerProvider({ children }: { children: ReactNode }) {
	// Idempotent: defer to an outer provider so the timer stays a single source of
	// truth even if the tree double-wraps.
	const parent = useContext(EntriesTimerContext);
	if (parent) return <>{children}</>;
	return <EntriesTimerProviderRoot>{children}</EntriesTimerProviderRoot>;
}

function EntriesTimerProviderRoot({ children }: { children: ReactNode }) {
	const timer = useTimerContext();
	const { makeApiRequest } = useApi();
	const { refresh } = useCacheMutations();

	const [binding, setBinding] = useState<Binding>(NO_BINDING);
	const [isBusy, setIsBusy] = useState(false);
	// Refs so the imperative actions read the latest binding/stopwatch without
	// depending on the per-second-changing `timer` object (which would churn every
	// callback's identity each tick and force needless re-renders).
	const bindingRef = useRef(binding);
	bindingRef.current = binding;
	const timerRef = useRef(timer);
	timerRef.current = timer;

	const applyBinding = useCallback((next: Binding) => {
		bindingRef.current = next;
		setBinding(next);
	}, []);

	// Pull the authoritative state from the server and rebind to it. The local
	// stopwatch is display only, so it always restarts from zero with the
	// server's live total as the base — using `startingDurationSeconds` would
	// silently drop however long the span had already been running.
	const syncFromServer = useCallback(async () => {
		try {
			const res = await makeApiRequest({
				endpoint: "/v2/timer",
				method: "GET",
			});
			if (res.status === "ERROR") return;
			const data = res.data as
				| { active?: boolean; entryId?: string; durationSeconds?: number }
				| undefined;
			const t = timerRef.current;
			if (data?.active && data.entryId) {
				t.dismiss();
				applyBinding({
					activeEntryId: data.entryId,
					baseUnitsSeconds: data.durationSeconds ?? 0,
				});
				t.start();
			} else if (bindingRef.current.activeEntryId !== null) {
				// Server has no open span — the local binding is stale.
				t.dismiss();
				applyBinding(NO_BINDING);
			}
		} catch {
			/* offline or the endpoint is unavailable — keep the local binding */
		}
	}, [makeApiRequest, applyBinding]);

	// Recover after a reload: the open span is the only record of a running timer,
	// so the binding is rebuilt from the server rather than persisted locally.
	// Also picks up a timer started on another device.
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only reconcile
	useEffect(() => {
		void syncFromServer();
	}, []);

	// Reconcile stale bindings: a binding with no running/paused stopwatch (e.g.
	// cleared from another tab, or the stopwatch finished) is dropped.
	useEffect(() => {
		if (timer.phase === "idle" && bindingRef.current.activeEntryId !== null) {
			applyBinding(NO_BINDING);
		}
	}, [timer.phase, applyBinding]);

	const refreshEntries = useCallback(() => refresh("entries"), [refresh]);

	// Open a server-side span on `entryId`. The server closes any span already
	// running (materializing its duration onto that entry), so a switch is one
	// call rather than persist-then-start. Returns the entry total the span opened
	// against, or null when the call failed — callers must not rebind on null or
	// the timer would tick against a span the server doesn't have.
	const startSpan = useCallback(
		async (entryId: string): Promise<number | null> => {
			try {
				const res = await makeApiRequest({
					endpoint: "/v2/timer/start",
					method: "POST",
					body: { entryId },
				});
				if (res.status === "ERROR") {
					toast.error(res.error || "Couldn't start the timer.");
					return null;
				}
				const data = res.data as
					| { startingDurationSeconds?: number }
					| undefined;
				return data?.startingDurationSeconds ?? 0;
			} catch {
				toast.error("Couldn't start the timer.");
				return null;
			}
		},
		[makeApiRequest],
	);

	// Close the running span. The server writes the accumulated duration onto the
	// entry, so there is no separate persist step. Idempotent server-side.
	const stopSpan = useCallback(async (): Promise<boolean> => {
		try {
			const res = await makeApiRequest({
				endpoint: "/v2/timer/stop",
				method: "POST",
			});
			if (res.status === "ERROR") {
				toast.error(res.error || "Couldn't save the timer's tracked time.");
				return false;
			}
			await refreshEntries();
			return true;
		} catch {
			toast.error("Couldn't save the timer's tracked time.");
			return false;
		}
	}, [makeApiRequest, refreshEntries]);

	// Float an entry to the top of the date-ordered list by bumping its end_time
	// to now. On a switch it lands above the entry just left (whose end_time keeps
	// its earlier start).
	const floatEntryToTop = useCallback(
		async (entryId: string) => {
			const res = await makeApiRequest({
				endpoint: `/v2/entries/${entryId}`,
				method: "PATCH",
				body: { touchEndTime: true },
			});
			if (res.status !== "ERROR") await refreshEntries();
		},
		[makeApiRequest, refreshEntries],
	);

	const startForEntry = useCallback(
		async (entry: Entry) => {
			const t = timerRef.current;
			const prev = bindingRef.current;
			if (isBusy) return;
			if (prev.activeEntryId === entry.id) return; // already timing this entry
			// Held for the whole call: two quick clicks on different rows would
			// otherwise open two spans, and the server would keep whichever landed
			// last while the stopwatch ticked against the other entry.
			setIsBusy(true);
			try {
				// One call switches: the server closes the previous span (writing its
				// duration onto that entry) and opens the new one. Abort on failure so
				// the previous timer keeps running rather than losing its elapsed time.
				const base = await startSpan(entry.id);
				if (base === null) return;
				t.dismiss(); // reset stopwatch to a clean 0 before rebinding
				applyBinding({ activeEntryId: entry.id, baseUnitsSeconds: base });
				t.start();
				if (prev.activeEntryId) await refreshEntries(); // previous duration landed
				void floatEntryToTop(entry.id); // jump the now-active entry to the top
			} finally {
				setIsBusy(false);
			}
		},
		[isBusy, startSpan, applyBinding, refreshEntries, floatEntryToTop],
	);

	const startNewBlankEntry = useCallback(async () => {
		if (isBusy) return;
		setIsBusy(true);
		try {
			const t = timerRef.current;
			const now = new Date();
			// Date-only (local) YYYY-MM-DD, matching every other entry so the stored
			// `date` column is midnight — a full ISO timestamp here stores a time
			// component and breaks exact-date queries/filters/reporting.
			const localDate = `${now.getFullYear()}-${String(
				now.getMonth() + 1,
			).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
			const res = await makeApiRequest({
				endpoint: "/v2/entries",
				method: "POST",
				body: {
					// Seed 1s so the row is immediately visible: the entries list
					// filters `units > 0`, and calculateUnits(0) === 0 would hide a
					// zero-duration entry until the timer accumulates. 1s → 0.1 units.
					// The binding base stays 0 so the toolbar still ticks from 00:00;
					// the first persist overwrites this seed with the real elapsed.
					date: localDate,
					unitsSeconds: 1,
					description: NARRATIVE_PLACEHOLDER,
					matterId: null,
					taskCode: null,
					activityCode: null,
				},
			});
			const newRawId = (res.data as { id?: number | string } | undefined)?.id;
			if (res.status === "ERROR" || newRawId == null) {
				toast.error(res.error || "Couldn't start the timer.");
				return;
			}
			// Opening the span also closes any previous one, so the entry being
			// switched away from gets its duration written without a separate PATCH.
			const newEntryId = formatEntryId(newRawId);
			const base = await startSpan(newEntryId);
			if (base === null) return;
			t.dismiss();
			applyBinding({ activeEntryId: newEntryId, baseUnitsSeconds: base });
			t.start();
			await refreshEntries();
		} finally {
			setIsBusy(false);
		}
	}, [isBusy, makeApiRequest, startSpan, applyBinding, refreshEntries]);

	// Pause is the only "finish" control — shown with a pause icon, but it behaves
	// like stop: persist the accumulated time onto the entry, drop the backend
	// gate, and unbind (back to idle) so a fresh timer can be started. To add more
	// time to that entry later, start the timer on its row again.
	const pause = useCallback(async () => {
		const t = timerRef.current;
		const prev = bindingRef.current;
		if (prev.activeEntryId) {
			// Closing the span is what writes the duration. Keep the timer bound if
			// it fails so the attorney can retry rather than losing the elapsed time.
			const ok = await stopSpan();
			if (!ok) return;
		}
		t.dismiss();
		applyBinding(NO_BINDING);
	}, [stopSpan, applyBinding]);

	// Deleting the timed entry stops the timer. The server already closes the span
	// as part of the delete, so this only unbinds the UI — no stop call, which
	// would race the row that just went away.
	const clearIfActive = useCallback(
		(entryIds: string[]) => {
			const activeId = bindingRef.current.activeEntryId;
			if (!activeId || !entryIds.includes(activeId)) return;
			timerRef.current.dismiss();
			applyBinding(NO_BINDING);
		},
		[applyBinding],
	);

	const active = binding.activeEntryId !== null;

	// Binding context: identity is stable across the per-second tick — it only
	// changes when the bound entry, phase, or busy flag change.
	const value = useMemo<EntriesTimerContextValue>(
		() => ({
			activeEntryId: binding.activeEntryId,
			isRunning: active && timer.phase === "running",
			isBusy,
			startForEntry,
			startNewBlankEntry,
			pause,
			syncFromServer,
			clearIfActive,
		}),
		[
			binding.activeEntryId,
			active,
			timer.phase,
			isBusy,
			startForEntry,
			startNewBlankEntry,
			pause,
			syncFromServer,
			clearIfActive,
		],
	);

	const displaySeconds = active
		? binding.baseUnitsSeconds + timer.elapsedSeconds
		: 0;

	return (
		<EntriesTimerContext.Provider value={value}>
			<EntriesTimerDisplayContext.Provider value={displaySeconds}>
				{children}
			</EntriesTimerDisplayContext.Provider>
		</EntriesTimerContext.Provider>
	);
}

/**
 * Non-throwing accessor for chrome rendered outside the provider — the toolbar
 * pill sits in the app shell, which the admin layout mounts without a timer.
 */
export function useEntriesTimerOptional():
	| EntriesTimerContextValue
	| undefined {
	return useContext(EntriesTimerContext);
}

export function useEntriesTimer(): EntriesTimerContextValue {
	const context = useContext(EntriesTimerContext);
	if (!context) {
		throw new Error(
			"useEntriesTimer must be used within an EntriesTimerProvider",
		);
	}
	return context;
}

/** Live base+elapsed seconds for the toolbar display (ticks every second). */
export function useEntriesTimerDisplaySeconds(): number {
	return useContext(EntriesTimerDisplayContext);
}
